import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { API } from "../lib/api";

const STAR_FILL = "#f59e0b";

/**
 * Public participant feedback page — no login required.
 *
 * Flow:
 *  - 1-3★ : shows a comment box, participant hits Submit → row saved,
 *           partner sees "Feedback submitted".
 *  - 4-5★ : submits immediately (no comment step) → response returns the
 *           partner's public review URL → we redirect the participant to
 *           Trustpilot / Google Business / etc.  Partner sees "Rated on
 *           Trust Pilot / Google".
 *  - Duplicate submission returns 409 and we render the Thank-you state.
 */
export default function PublicFeedback() {
  const { bookingId } = useParams();
  const [meta, setMeta] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { rating, redirect_url, already }
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    fetch(`${API}/public/feedback/${bookingId}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d)))
      .then(d => {
        if (!mounted) return;
        setMeta(d);
        if (d.submitted) setDone({ rating: d.rating, redirect_url: "", already: true });
      })
      .catch(e => mounted && setErr(e?.detail || "Feedback link not recognised"));
    return () => { mounted = false; };
  }, [bookingId]);

  const submit = async (finalRating) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/feedback/${bookingId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating: finalRating, comment: finalRating <= 3 ? comment : "" }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Submit failed");
      setDone({ rating: finalRating, redirect_url: d.redirect_url });
      if (d.redirect_url) {
        // Open the partner's public review site in a NEW tab per Founder's
        // spec — never hijack the participant's current tab.
        window.open(d.redirect_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) { setErr(e?.message || "Submit failed"); }
    setBusy(false);
  };

  const onStarClick = (star) => {
    setRating(star);
    if (star >= 4) submit(star);
  };

  if (err) return (
    <Shell>
      <div className="text-center space-y-2">
        <AlertTriangle size={44} className="mx-auto text-amber-500"/>
        <h2 className="text-xl font-display font-semibold">Link not valid</h2>
        <p className="text-sm text-[color:var(--aii-muted)]">{err}</p>
      </div>
    </Shell>
  );
  if (!meta) return <Shell><p className="text-center text-[color:var(--aii-muted)]">Loading…</p></Shell>;
  if (done) {
    return (
      <Shell>
        <div className="text-center space-y-3" data-testid="fb-thank-you">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500"/>
          <h2 className="text-2xl font-display font-semibold">Thank you!</h2>
          {done.already && <p className="text-[color:var(--aii-muted)]">You&apos;ve already shared feedback for this booking.</p>}
          {done.redirect_url && <p className="text-[color:var(--aii-muted)]">Redirecting you to leave a public review…</p>}
          {!done.redirect_url && !done.already && <p className="text-[color:var(--aii-muted)]">Your feedback has been sent to <b>{meta.partner_name}</b>.</p>}
        </div>
      </Shell>
    );
  }

  const showComment = rating > 0 && rating <= 3;
  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Feedback</div>
        <h1 className="text-2xl font-display font-semibold mt-1">Rate your experience</h1>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">
          {meta.participant_name && <>Hi {meta.participant_name.split(" ")[0]}! </>}
          How was your experience with <b>{meta.partner_name}</b>?
        </p>
      </div>
      <div className="flex items-center justify-center gap-1 mb-6" data-testid="fb-stars">
        {[1,2,3,4,5].map(s => {
          const active = s <= (hover || rating);
          return (
            <button
              key={s}
              type="button"
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              onClick={() => onStarClick(s)}
              disabled={busy}
              data-testid={`fb-star-${s}`}
              className="p-1 hover:scale-110 transition disabled:opacity-50"
              aria-label={`Rate ${s} star${s === 1 ? "" : "s"}`}
            >
              <Star size={44} className={active ? "text-amber-500" : "text-slate-300"}
                    fill={active ? STAR_FILL : "none"} strokeWidth={1.5}/>
            </button>
          );
        })}
      </div>
      {showComment && (
        <div className="space-y-3" data-testid="fb-comment-block">
          <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">
            Suggestions &amp; feedback
          </label>
          <textarea
            rows={4}
            className="input-aii w-full"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Tell us what could have been better…"
            data-testid="fb-comment"
          />
          <button
            onClick={() => submit(rating)}
            disabled={busy}
            data-testid="fb-submit"
            className="w-full px-4 py-3 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send size={14}/> {busy ? "Submitting…" : "Submit feedback"}
          </button>
          <p className="text-[10px] text-[color:var(--aii-muted)] text-center">
            Your feedback goes privately to {meta.partner_name} so they can improve.
          </p>
        </div>
      )}
      {rating >= 4 && busy && (
        <div className="text-center text-sm text-[color:var(--aii-muted)] italic">Submitting your rating…</div>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[color:var(--aii-bg)] flex items-center justify-center p-4">
    <div className="max-w-md w-full aii-card p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-4">
        <img src="/aii-logo.png" alt="Aii Group" className="w-8 h-8 object-contain"/>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Aii Group Feedback</div>
      </div>
      {children}
    </div>
  </div>
);
